import React from 'react';
import { Bell, Package, CreditCard, MessageCircle, Star, AlertCircle, X } from 'lucide-react';
import { useGetNotifications } from '../../../services/notification.service';
import { formatDistanceToNow } from 'date-fns';

const getNotificationStyles = (type) => {
  switch (type) {
    case 'order_created':
    case 'order_updated':
    case 'order_completed':
      return { icon: <Package className="w-5 h-5" />, color: 'bg-purple-100 text-purple-800' };
    case 'order_cancelled':
      return { icon: <X className="w-5 h-5" />, color: 'bg-red-100 text-red-800' };
    case 'payment_received':
      return { icon: <CreditCard className="w-5 h-5" />, color: 'bg-green-100 text-green-800' };
    case 'message_received':
      return { icon: <MessageCircle className="w-5 h-5" />, color: 'bg-blue-100 text-blue-800' };
    case 'review_received':
      return { icon: <Star className="w-5 h-5" />, color: 'bg-yellow-100 text-yellow-800' };
    case 'account_blocked':
    case 'account_unblocked':
      return { icon: <AlertCircle className="w-5 h-5" />, color: 'bg-orange-100 text-orange-800' };
    default:
      return { icon: <Bell className="w-5 h-5" />, color: 'bg-gray-100 text-gray-800' };
  }
}

const NotificationItem = ({ notification }) => {
  const styles = getNotificationStyles(notification.type);
  const time = notification.createdAt ? formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true }) : '';

  return (
    <div className="flex items-center space-x-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer">
      <div className="flex-shrink-0">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${styles.color}`}>
          {styles.icon}
        </div>
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {notification.title}
        </p>
        <div className="flex items-center space-x-2 mt-1">
          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">{notification.message}</p>
          <span className="text-xs text-gray-400 dark:text-gray-500">•</span>
          <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{time}</p>
        </div>
      </div>
    </div>
  );
};

const UnreadNotifications = () => {
  const { data, isLoading } = useGetNotifications();
  const unreadNotifications = (data?.notifications || [])
    .filter(n => !n.isRead)
    .slice(0, 5);

  return (
    <div className="bg-white dark:bg-[#1d2026] rounded-[20px]  border border-gray-100 dark:border-[#32353c] overflow-hidden w-full md:w-[35%]">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-[#32353c]">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Unread Notifications</h2>
        <a href="/notifications" className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">
          See all
        </a>
      </div>
      
      {/* Notifications List */}
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          </div>
        ) : unreadNotifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400 text-sm">
            No unread notifications
          </div>
        ) : (
          unreadNotifications.map((notification) => (
            <NotificationItem
              key={notification._id}
              notification={notification}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default UnreadNotifications;